// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';

import type { BffWebSocketClient } from '@/services/bffWebSocket';
import type { ViewerRouteParams } from '@/hooks/useInviteViewer';
import { withViewerParams } from '@/services/tourInviteLink';

type UseHoleActionsParams = {
  debug: boolean;
  holeNumber: number;
  roundId: string | null;
  canMutateRoundStatus: boolean;
  /** Keeps a spectator spectating when they step to the next hole; empty for everyone else. */
  viewerRouteParams: ViewerRouteParams;
  router: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
  selectedGameDefinitionIds: string[];
  setHole: (hole: number) => void;
  socketClient: BffWebSocketClient | null;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

export const useHoleActions = ({
  debug,
  holeNumber,
  roundId,
  canMutateRoundStatus,
  viewerRouteParams,
  router,
  selectedGameDefinitionIds,
  setHole,
  socketClient,
  logWithContext,
}: UseHoleActionsParams) => {
  const navigateToHole = useCallback(
    (targetHole: number) => {
      if (!roundId) return;
      setHole(targetHole);
      const params: string[] = [];
      if (debug) params.push('debug=true');
      if (selectedGameDefinitionIds.length > 0) {
        params.push(`gameDefinitionIds=${encodeURIComponent(selectedGameDefinitionIds.join(','))}`);
      }
      const dir = targetHole > holeNumber ? 1 : -1;
      params.push(`dir=${dir}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(withViewerParams(`/round/${roundId}/hole/${targetHole}/compact${query}`, viewerRouteParams));
    },
    [debug, holeNumber, roundId, router, selectedGameDefinitionIds, setHole, viewerRouteParams]
  );

  const finishRound = useCallback(() => {
    if (!socketClient || !roundId) return;

    if (canMutateRoundStatus) {
      // 'queued' is not a failure — the frame is flushed when the socket (re)opens.
      const sent = socketClient.send({ type: 'round.status.update', roundId, status: 'finished' });
      if (sent !== 'sent') {
        logWithContext('hole.round.finishQueued', { roundId }, 'warn');
      }
    }

    const params: string[] = ['from=hole'];
    if (debug) params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/summary${query}`);
  }, [canMutateRoundStatus, debug, logWithContext, roundId, router, socketClient]);

  return {
    finishRound,
    navigateToHole,
  };
};
